import { toArray } from "@repo/utils"

/**
 * Basic configuration shared by all schema types
 */
export interface BaseSchemaConfig {
  /**
   * The unique identifier for the entity
   */
  id?: string
  /**
   * The URL of the page
   */
  url?: string
  /**
   * The name of the item
   */
  name?: string
  /**
   * A description of the item
   */
  description?: string
  /**
   * Image URL representing the item
   */
  image?: string | string[]
}

/**
 * Schema.org Organization type
 */
export interface OrganizationSchema extends BaseSchemaConfig {
  /**
   * The logo of the organization
   */
  logo?: string
  /**
   * Social media profiles
   */
  sameAs?: string[]
  /**
   * Contact information
   */
  contactPoint?: {
    /**
     * Type of contact point
     */
    contactType: string
    /**
     * Telephone number
     */
    telephone: string
    /**
     * Email address
     */
    email?: string
  }[]
}

/**
 * Schema.org Article type
 */
export interface ArticleSchema extends BaseSchemaConfig {
  /**
   * The headline of the article
   */
  headline: string
  /**
   * The author of the article
   */
  author: {
    /**
     * The type of the author ("Person" or "Organization")
     */
    type: "Person" | "Organization"
    /**
     * The name of the author
     */
    name: string
    /**
     * The URL associated with the author
     */
    url?: string
  }
  /**
   * The publisher of the article
   */
  publisher?: {
    /**
     * The type of the publisher (typically "Organization")
     */
    type: "Organization"
    /**
     * The name of the publisher
     */
    name: string
    /**
     * The logo of the publisher
     */
    logo?: string
  }
  /**
   * When the article was published
   */
  datePublished: string
  /**
   * When the article was last modified
   */
  dateModified?: string
  /**
   * Keywords associated with the article
   */
  keywords?: string[]
}

/**
 * Schema.org FAQPage type
 */
export interface FAQPageSchema extends BaseSchemaConfig {
  /**
   * List of questions and answers
   */
  mainEntity: Array<{
    /**
     * The question text
     */
    question: string
    /**
     * The answer text (can include HTML)
     */
    answer: string
  }>
}

/**
 * Schema.org Product type
 */
export interface ProductSchema extends BaseSchemaConfig {
  /**
   * The brand of the product
   */
  brand?: {
    /**
     * The name of the brand
     */
    name: string
  }
  /**
   * Offers associated with the product
   */
  offers?: {
    /**
     * The price of the offer
     */
    price: number
    /**
     * The currency of the price (ISO 4217)
     */
    priceCurrency: string
    /**
     * The URL where the offer can be obtained
     */
    url?: string
    /**
     * The availability of the product
     */
    availability?: "InStock" | "OutOfStock" | "PreOrder"
    /**
     * The date when the offer expires
     */
    priceValidUntil?: string
  }
  /**
   * Aggregate rating for the product
   */
  aggregateRating?: {
    /**
     * The value of the rating
     */
    ratingValue: number
    /**
     * The best possible rating
     */
    bestRating?: number
    /**
     * The worst possible rating
     */
    worstRating?: number
    /**
     * The number of ratings
     */
    ratingCount: number
    /**
     * The number of reviews
     */
    reviewCount?: number
  }
}

/**
 * Schema.org BreadcrumbList type
 */
export interface BreadcrumbListSchema extends BaseSchemaConfig {
  /**
   * List of breadcrumb items
   */
  itemListElement: Array<{
    /**
     * The position of the breadcrumb
     */
    position: number
    /**
     * The name of the breadcrumb
     */
    name: string
    /**
     * The URL of the breadcrumb
     */
    item: string
  }>
}

/**
 * Schema.org LocalBusiness type
 */
export interface LocalBusinessSchema extends OrganizationSchema {
  /**
   * The geo coordinates of the business
   */
  geo?: {
    /**
     * The latitude
     */
    latitude: number
    /**
     * The longitude
     */
    longitude: number
  }
  /**
   * The address of the business
   */
  address?: {
    /**
     * The street address
     */
    streetAddress?: string
    /**
     * The locality/city
     */
    addressLocality?: string
    /**
     * The region/state
     */
    addressRegion?: string
    /**
     * The postal code
     */
    postalCode?: string
    /**
     * The country
     */
    addressCountry?: string
  }
  /**
   * Opening hours specification
   */
  openingHoursSpecification?: Array<{
    /**
     * The day of the week (e.g., "Monday")
     */
    dayOfWeek: string[]
    /**
     * The opening time
     */
    opens: string
    /**
     * The closing time
     */
    closes: string
  }>
  /**
   * The telephone number
   */
  telephone?: string
  /**
   * The price range
   */
  priceRange?: string
}

/**
 * Schema.org WebSite type
 */
export interface WebsiteSchema extends BaseSchemaConfig {
  /**
   * The search URL template
   */
  potentialAction?: {
    /**
     * The search URL template
     */
    target: string
    /**
     * The query input name
     */
    "query-input": string
  }
}

/**
 * Configuration for any schema object
 */
export type SchemaConfig =
  | {
      type: "Organization"
      data: OrganizationSchema
    }
  | {
      type: "Article"
      data: ArticleSchema
    }
  | {
      type: "FAQPage"
      data: FAQPageSchema
    }
  | {
      type: "Product"
      data: ProductSchema
    }
  | {
      type: "BreadcrumbList"
      data: BreadcrumbListSchema
    }
  | {
      type: "LocalBusiness"
      data: LocalBusinessSchema
    }
  | {
      type: "WebSite"
      data: WebsiteSchema
    }
  | {
      type: string
      data: Record<string, unknown>
    }

/**
 * Generates a JSON-LD schema object from the provided configuration
 */
export const generateSchema = (
  config: SchemaConfig,
): Record<string, unknown> => {
  const { type, data } = config

  return {
    "@context": "https://schema.org",
    "@type": type,
    ...data,
  }
}

type StructuredDataResponse =
  | Record<string, unknown>
  | Record<string, unknown>[]

/**
 * Returns the schema as a JSON-LD object
 */
export const generateStructuredData = (
  config: SchemaConfig | SchemaConfig[],
): StructuredDataResponse => {
  const configs = toArray(config)
  const schemas = configs.map(generateSchema)
  const jsonContent = schemas.length === 1 ? schemas[0] : schemas

  return jsonContent
}

/**
 * Common schema.org types to use with the schema generator
 */
export const SchemaTypes = {
  Organization: "Organization",
  Article: "Article",
  FAQPage: "FAQPage",
  Product: "Product",
  BreadcrumbList: "BreadcrumbList",
  Person: "Person",
  LocalBusiness: "LocalBusiness",
  WebSite: "WebSite",
  Recipe: "Recipe",
  Event: "Event",
  VideoObject: "VideoObject",
  ImageObject: "ImageObject",
} as const

/**
 * Helper to create an organization schema
 */
export const createOrganizationSchema = (
  data: OrganizationSchema,
): SchemaConfig => ({
  type: SchemaTypes.Organization,
  data,
})

/**
 * Helper to create an article schema
 */
export const createArticleSchema = (data: ArticleSchema): SchemaConfig => ({
  type: SchemaTypes.Article,
  data,
})

/**
 * Helper to create a FAQ page schema
 */
export const createFAQPageSchema = (data: FAQPageSchema): SchemaConfig => ({
  type: SchemaTypes.FAQPage,
  data,
})

/**
 * Helper to create a product schema
 */
export const createProductSchema = (data: ProductSchema): SchemaConfig => ({
  type: SchemaTypes.Product,
  data,
})

/**
 * Helper to create a breadcrumb list schema
 */
export const createBreadcrumbListSchema = (
  data: BreadcrumbListSchema,
): SchemaConfig => ({
  type: SchemaTypes.BreadcrumbList,
  data,
})

/**
 * Helper to create a local business schema
 */
export const createLocalBusinessSchema = (
  data: LocalBusinessSchema,
): SchemaConfig => ({
  type: SchemaTypes.LocalBusiness,
  data,
})

/**
 * Helper to create a website schema
 */
export const createWebsiteSchema = (data: WebsiteSchema): SchemaConfig => ({
  type: SchemaTypes.WebSite,
  data,
})
